import {
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useState,
  type JSX,
} from "react";

export type UiOverlayContextData = {
  /** The setter for the content of the overlay */
  setContent(content: JSX.Element | undefined): void;

  /** How much space is available in the bottom right overlay UI, that is not already taken up by the AllModesOverlay. */
  bottomRightAvailableHeight?: number;

  /** Setter for bottomRightAvailableHeight. Should only be used by the AllModesOverlay owning this space. */
  setBottomRightAvailableHeight(height?: number): void;
};

export const UiOverlayContext = createContext<UiOverlayContextData | undefined>(
  undefined,
);

/**
 * @returns a context useful to set an element in the DOM in overlay on the app, while preserving all contexts
 */
export function useUiOverlayContext(): UiOverlayContextData {
  const context = useContext(UiOverlayContext);
  if (!context) {
    throw new Error("UiOverlayContext is not initialized.");
  }
  return context;
}

/**
 * @returns A custom UI element that can be rendered on top of the app
 */
export function UiOverlayContextProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const [content, setContent] = useState<JSX.Element | undefined>();
  const [bottomRightAvailableHeight, setBottomRightAvailableHeight] = useState<
    number | undefined
  >();

  const value = useMemo<UiOverlayContextData>(
    () => ({
      setContent,
      bottomRightAvailableHeight,
      setBottomRightAvailableHeight,
    }),
    [bottomRightAvailableHeight],
  );

  return (
    <UiOverlayContext value={value}>
      {content}
      {children}
    </UiOverlayContext>
  );
}
